import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { DateTime } from 'luxon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Rectangle } from 'recharts';
import { getCohortRetention, getProgramRetention, getKidsWithPrograms } from '../../api/admin';
import { SEASONS, SPORTS, PROGRAM_TYPES } from '../../constants/constants';
import { ORG_NAMES } from '../../constants/constants';
import Loader from '../common/Loader';
import RetentionKidModal from './RetentionKidModal';
import './Retention.css';

const currentYear = DateTime.now().year;
const yearOptions = Array.from({ length: currentYear - 2024 + 2 }, (_, i) => ({
  value: 2024 + i,
  label: String(2024 + i)
}));

const seasonOptions = SEASONS.map(s => ({ value: s.toLowerCase(), label: s }));

const orgOptions = Object.entries(ORG_NAMES).map(([value, label]) => ({
  value,
  label
}));

const sportOptions = SPORTS.map(s => ({
  value: s,
  label: s.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}));

const typeOptions = PROGRAM_TYPES.map(t => ({
  value: t,
  label: t.charAt(0).toUpperCase() + t.slice(1)
}));

const BAR_COLORS = ['#28a745', '#007bff', '#17a2b8', '#6f42c1', '#fd7e14', '#20c997', '#dc3545', '#ffc107'];

// Custom background that extends to full chart height and handles clicks
const ClickableBackground = (props) => {
  const { x, y, width, height, payload, onClick } = props;
  // Extend background to top of chart area (y=0) while keeping same width
  const fullHeight = y + height;
  return (
    <Rectangle
      x={x}
      y={0}
      width={width}
      height={fullHeight}
      fill="transparent"
      cursor="pointer"
      onClick={() => onClick && onClick(payload)}
    />
  );
};

function Retention() {
  const [loading, setLoading] = useState(false);
  const [retentionData, setRetentionData] = useState(null);

  // Filter states
  const [startYear, setStartYear] = useState({ value: currentYear, label: String(currentYear) });
  const [startSeason, setStartSeason] = useState(null);
  const [orgFilter, setOrgFilter] = useState([]);
  const [sportFilter, setSportFilter] = useState([]);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalKids, setModalKids] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('cohort');

  // Program Analysis states
  const [programLoading, setProgramLoading] = useState(false);
  const [programData, setProgramData] = useState(null);
  const [programYear, setProgramYear] = useState({ value: currentYear, label: String(currentYear) });
  const [programSeason, setProgramSeason] = useState(null);
  const [programOrgFilter, setProgramOrgFilter] = useState([]);
  const [programSportFilter, setProgramSportFilter] = useState([]);
  const [programTypeFilter, setProgramTypeFilter] = useState(null);
  const [sortField, setSortField] = useState('retentionRate');
  const [sortDirection, setSortDirection] = useState('desc');

  const canAnalyze = startYear && startSeason;
  const canAnalyzePrograms = programYear;

  const handleAnalyze = async () => {
    if (!canAnalyze) return;

    setLoading(true);
    setRetentionData(null);

    try {
      const org = orgFilter.length > 0 ? orgFilter.map(o => o.value) : undefined;
      const sport = sportFilter.length > 0 ? sportFilter.map(s => s.value) : undefined;

      const response = await getCohortRetention(
        startYear.value,
        startSeason.value,
        org,
        sport
      );

      if (response?.data) {
        setRetentionData(response.data);
      }
    } catch (error) {
      console.error('Error fetching cohort retention:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = useMemo(() => {
    if (!retentionData?.cohort) return [];

    // Start with the cohort itself as first bar (100% baseline)
    const cohortBar = {
      label: `${retentionData.cohort.season.charAt(0).toUpperCase() + retentionData.cohort.season.slice(1)} ${retentionData.cohort.year}`,
      returnedCount: retentionData.cohort.totalKids,
      returnedKidIds: retentionData.cohort.kidIds,
      year: retentionData.cohort.year,
      season: retentionData.cohort.season,
      isStartingCohort: true
    };

    const subsequentBars = (retentionData.seasonSnapshots || []).map(snapshot => ({
      label: `${snapshot.season.charAt(0).toUpperCase() + snapshot.season.slice(1)} ${snapshot.year}`,
      returnedCount: snapshot.returnedCount,
      returnedKidIds: snapshot.returnedKidIds,
      year: snapshot.year,
      season: snapshot.season
    }));

    return [cohortBar, ...subsequentBars];
  }, [retentionData]);

  const overallRetentionPercent = useMemo(() => {
    if (!retentionData?.cohort?.totalKids) return 0;
    const neverReturned = retentionData.neverReturnedKidIds?.length || 0;
    const total = retentionData.cohort.totalKids;
    const returned = total - neverReturned;
    return total > 0 ? Math.round((returned / total) * 100) : 0;
  }, [retentionData]);

  const seasonsReturnedMap = useMemo(() => {
    if (!retentionData?.seasonSnapshots) return {};
    const countMap = {};
    retentionData.seasonSnapshots.forEach(snapshot => {
      (snapshot.returnedKidIds || []).forEach(kidId => {
        countMap[kidId] = (countMap[kidId] || 0) + 1;
      });
    });
    return countMap;
  }, [retentionData]);

  const totalSeasons = retentionData?.seasonSnapshots?.length || 0;

  const handleBarClick = async (data) => {
    if (!data?.returnedKidIds?.length) return;

    setModalLoading(true);
    setShowModal(true);
    const title = data.isStartingCohort
      ? `Starting Cohort: ${data.label} (${data.returnedCount} kids)`
      : `Returned in ${data.label} (${data.returnedCount} kids)`;
    setModalTitle(title);

    try {
      const response = await getKidsWithPrograms(data.returnedKidIds, data.year, data.season);
      if (response?.data) {
        setModalKids(response.data);
      }
    } catch (error) {
      console.error('Error fetching kids:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleNeverReturnedClick = async () => {
    if (!retentionData?.neverReturnedKidIds?.length) return;

    setModalLoading(true);
    setShowModal(true);
    setModalTitle(`Never Returned (${retentionData.neverReturnedKidIds.length} kids)`);

    try {
      // For never returned, show the program from the starting cohort season
      const response = await getKidsWithPrograms(
        retentionData.neverReturnedKidIds,
        retentionData.cohort?.year,
        retentionData.cohort?.season
      );
      if (response?.data) {
        setModalKids(response.data);
      }
    } catch (error) {
      console.error('Error fetching kids:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalKids([]);
    setModalTitle('');
  };

  // Program Analysis handlers
  const handleAnalyzePrograms = async () => {
    if (!canAnalyzePrograms) return;

    setProgramLoading(true);
    setProgramData(null);

    try {
      const org = programOrgFilter.length > 0 ? programOrgFilter.map(o => o.value) : undefined;
      const sport = programSportFilter.length > 0 ? programSportFilter.map(s => s.value) : undefined;
      const type = programTypeFilter?.value || undefined;

      const response = await getProgramRetention(
        programYear.value,
        programSeason?.value,
        org,
        sport,
        type
      );

      if (response?.data) {
        setProgramData(response.data);
      }
    } catch (error) {
      console.error('Error fetching program retention:', error);
    } finally {
      setProgramLoading(false);
    }
  };

  const sortedPrograms = useMemo(() => {
    if (!programData?.programs) return [];
    const sorted = [...programData.programs];
    sorted.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (sortDirection === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [programData, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleProgramClick = async (program) => {
    if (!program.cohortSize) return;

    setModalLoading(true);
    setShowModal(true);
    setModalTitle(`${program.programTitle} (${program.returnedCount}/${program.cohortSize} returned)`);

    try {
      const allKidIds = [...program.returnedKidIds, ...program.notReturnedKidIds];
      const response = await getKidsWithPrograms(allKidIds, program.year, program.season);
      if (response?.data) {
        const kids = response.data.map(kid => ({
          ...kid,
          returned: program.returnedKidIds.includes(kid.id)
        }));
        kids.sort((a, b) => (b.returned ? 1 : 0) - (a.returned ? 1 : 0));
        setModalKids(kids);
      }
    } catch (error) {
      console.error('Error fetching kids:', error);
    } finally {
      setModalLoading(false);
    }
  };

  const getRetentionColor = (rate) => {
    if (rate >= 0.5) return '#28a745';
    if (rate >= 0.25) return '#ffc107';
    return '#dc3545';
  };

  return (
    <div className="retention-page">
      <h1>Retention Dashboard</h1>

      <div className="retention-tabs">
        <button
          className={`retention-tab ${activeTab === 'cohort' ? 'active' : ''}`}
          onClick={() => setActiveTab('cohort')}
        >
          Cohort Analysis
        </button>
        <button
          className={`retention-tab ${activeTab === 'program' ? 'active' : ''}`}
          onClick={() => setActiveTab('program')}
        >
          Program Analysis
        </button>
      </div>

      {activeTab === 'cohort' && (
        <>
          <div className="retention-filters">
        <div className="retention-filter-group">
          <label>Year *</label>
          <Select
            value={startYear}
            onChange={setStartYear}
            options={yearOptions}
            className="retention-filter-select"
            classNamePrefix="filter"
            placeholder="Select year"
          />
        </div>
        <div className="retention-filter-group">
          <label>Season *</label>
          <Select
            value={startSeason}
            onChange={setStartSeason}
            options={seasonOptions}
            className="retention-filter-select"
            classNamePrefix="filter"
            placeholder="Select season"
            isClearable
          />
        </div>
        <div className="retention-filter-group">
          <label>Org</label>
          <Select
            value={orgFilter}
            onChange={setOrgFilter}
            options={orgOptions}
            isMulti
            className="retention-filter-select"
            classNamePrefix="filter"
            placeholder="All orgs"
          />
        </div>
        <div className="retention-filter-group">
          <label>Sport</label>
          <Select
            value={sportFilter}
            onChange={setSportFilter}
            options={sportOptions}
            isMulti
            className="retention-filter-select"
            classNamePrefix="filter"
            placeholder="All sports"
          />
        </div>
        <div className="retention-filter-group retention-filter-button">
          <button
            className="analyze-button"
            onClick={handleAnalyze}
            disabled={!canAnalyze || loading}
          >
            {loading ? 'Analyzing...' : 'Analyze Cohort'}
          </button>
        </div>
      </div>

      {loading && <Loader size={100} />}

      {!loading && retentionData && (
        <>
          <div className="retention-summary">
            <div className="summary-card">
              <h3>Cohort Size</h3>
              <p className="summary-value">{retentionData.cohort?.totalKids || 0} kids</p>
              <p className="summary-subtitle">
                {startSeason?.label} {startYear?.label}
              </p>
            </div>
            <div className="summary-card">
              <h3>Overall Retention</h3>
              <p className="summary-value retention-percent">{overallRetentionPercent}%</p>
              <p className="summary-subtitle">returned in any subsequent season</p>
            </div>
          </div>

          {chartData.length > 0 && (
            <div className="retention-chart-section">
              <h2>Retention by Season</h2>
              <p className="chart-instruction">Click anywhere in a column to see the kids for that season</p>
              <div className="retention-chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Returned Kids', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      formatter={(value) => [`${value} kids`, 'Returned']}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px'
                      }}
                    />
                    <Bar
                      dataKey="returnedCount"
                      cursor="pointer"
                      onClick={(data) => handleBarClick(data)}
                      background={<ClickableBackground onClick={handleBarClick} />}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={BAR_COLORS[index % BAR_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {retentionData.neverReturnedKidIds?.length > 0 && (
            <div className="never-returned-section">
              <button
                className="never-returned-button"
                onClick={handleNeverReturnedClick}
              >
                View Never Returned ({retentionData.neverReturnedKidIds.length} kids)
              </button>
            </div>
          )}
        </>
      )}
        </>
      )}

      {activeTab === 'program' && (
        <>
          <div className="retention-filters">
            <div className="retention-filter-group">
              <label>Year *</label>
              <Select
                value={programYear}
                onChange={setProgramYear}
                options={yearOptions}
                className="retention-filter-select"
                classNamePrefix="filter"
                placeholder="Select year"
              />
            </div>
            <div className="retention-filter-group">
              <label>Season</label>
              <Select
                value={programSeason}
                onChange={setProgramSeason}
                options={seasonOptions}
                className="retention-filter-select"
                classNamePrefix="filter"
                placeholder="All seasons"
                isClearable
              />
            </div>
            <div className="retention-filter-group">
              <label>Org</label>
              <Select
                value={programOrgFilter}
                onChange={setProgramOrgFilter}
                options={orgOptions}
                isMulti
                className="retention-filter-select"
                classNamePrefix="filter"
                placeholder="All orgs"
              />
            </div>
            <div className="retention-filter-group">
              <label>Sport</label>
              <Select
                value={programSportFilter}
                onChange={setProgramSportFilter}
                options={sportOptions}
                isMulti
                className="retention-filter-select"
                classNamePrefix="filter"
                placeholder="All sports"
              />
            </div>
            <div className="retention-filter-group">
              <label>Type</label>
              <Select
                value={programTypeFilter}
                onChange={setProgramTypeFilter}
                options={typeOptions}
                className="retention-filter-select"
                classNamePrefix="filter"
                placeholder="All types"
                isClearable
              />
            </div>
            <div className="retention-filter-group retention-filter-button">
              <button
                className="analyze-button"
                onClick={handleAnalyzePrograms}
                disabled={!canAnalyzePrograms || programLoading}
              >
                {programLoading ? 'Analyzing...' : 'Analyze Programs'}
              </button>
            </div>
          </div>

          {programLoading && <Loader size={100} />}

          {!programLoading && programData && (
            <>
              <div className="retention-summary">
                <div className="summary-card">
                  <h3>Programs</h3>
                  <p className="summary-value">{programData.summary?.totalPrograms || 0}</p>
                  <p className="summary-subtitle">in {programYear?.label}</p>
                </div>
                <div className="summary-card">
                  <h3>Avg Retention</h3>
                  <p className="summary-value retention-percent">
                    {Math.round((programData.summary?.avgRetentionRate || 0) * 100)}%
                  </p>
                  <p className="summary-subtitle">kids returned later</p>
                </div>
              </div>

              {sortedPrograms.length > 0 && (
                <div className="program-retention-table-section">
                  <p className="chart-instruction">Click a row to see kids from that program</p>
                  <table className="program-retention-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('programTitle')} className="sortable">
                          Program {sortField === 'programTitle' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th>Org</th>
                        <th>Sport</th>
                        <th>Season</th>
                        <th onClick={() => handleSort('cohortSize')} className="sortable">
                          Roster {sortField === 'cohortSize' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSort('returnedCount')} className="sortable">
                          Returned {sortField === 'returnedCount' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                        <th onClick={() => handleSort('retentionRate')} className="sortable">
                          Rate {sortField === 'retentionRate' && (sortDirection === 'asc' ? '↑' : '↓')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPrograms.map(program => (
                        <tr
                          key={program.programId}
                          onClick={() => handleProgramClick(program)}
                          className="clickable-row"
                        >
                          <td>{program.programTitle}</td>
                          <td>{ORG_NAMES[program.org] || program.org}</td>
                          <td>{program.sport}</td>
                          <td>{program.season} {program.year}</td>
                          <td>{program.cohortSize}</td>
                          <td>{program.returnedCount}</td>
                          <td style={{ color: getRetentionColor(program.retentionRate), fontWeight: 'bold' }}>
                            {Math.round(program.retentionRate * 100)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      <RetentionKidModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={modalTitle}
        kids={modalKids}
        loading={modalLoading}
        seasonsReturnedMap={seasonsReturnedMap}
        totalSeasons={totalSeasons}
      />
    </div>
  );
}

export default Retention;
